import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatPercent, getStatusColor, timeAgo } from '@/lib/utils';
import { ErrorState } from '@/components/shared/ErrorState';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Wrench,
  DollarSign,
  Wind,
  Sun,
  Battery,
  ChevronRight,
  Zap,
  ThermometerSun,
  Clock,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const assetIcons = {
  wind: Wind,
  solar: Sun,
  battery: Battery,
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, alertsRes, workOrdersRes, trendRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/alerts`),
        axios.get(`${API}/work-orders`),
        axios.get(`${API}/dashboard/performance-trend?days=7`),
      ]);
      setStats(statsRes.data);
      setAlerts(alertsRes.data.slice(0, 4));
      setWorkOrders(workOrdersRes.data.slice(0, 5));
      setPerformanceData(
        (trendRes.data.series || []).map((d) => ({
          day: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }),
          availability: d.availability_pct,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-500">Fleet overview and operational status</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            Live Data
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Fleet Availability */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="kpi-availability">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Fleet Availability</span>
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <Activity className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <div className="text-3xl font-heading font-bold text-slate-900">
                {formatPercent(stats?.fleet_availability || 0)}
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                <span>{stats?.total_assets || 0} total assets</span>
              </div>
            </CardContent>
          </Card>

          {/* Revenue MTD */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="kpi-revenue">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Revenue (MTD)</span>
                <div className="p-2 bg-sky-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-sky-600" />
                </div>
              </div>
              <div className="text-3xl font-heading font-bold text-slate-900">
                {formatCurrency(stats?.revenue_mtd || 0, true)}
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                <span>YTD: {formatCurrency(stats?.revenue_ytd || 0, true)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Active Alerts */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="kpi-alerts">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Active Alerts</span>
                <div className="p-2 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
              </div>
              <div className="text-3xl font-heading font-bold text-slate-900">
                {stats?.active_alerts || 0}
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                <span>{stats?.critical_alerts || 0} critical</span>
              </div>
            </CardContent>
          </Card>

          {/* Open Work Orders */}
          <Card className="border-slate-200 hover:shadow-md transition-shadow" data-testid="kpi-workorders">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Work Orders</span>
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Wrench className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <div className="text-3xl font-heading font-bold text-slate-900">
                {stats?.open_work_orders || 0}
              </div>
              <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                <Clock className="w-3 h-3" />
                <span>{stats?.overdue_work_orders || 0} overdue</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Performance Chart */}
          <Card className="lg:col-span-2 border-slate-200" data-testid="performance-chart">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg flex items-center justify-between">
                7-Day Performance Trend
                <Badge variant="outline" className="font-normal">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Live
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="colorAvailability" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#64748b' }} />
                  <YAxis
                    domain={[95, 100]}
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [`${value}%`, 'Availability']}
                  />
                  <Area
                    type="monotone"
                    dataKey="availability"
                    stroke="#0ea5e9"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorAvailability)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Fleet Status */}
          <Card className="border-slate-200" data-testid="fleet-status">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg">Fleet Status</CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Status Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-emerald-700">{stats?.operational_assets || 0}</p>
                  <p className="text-sm text-emerald-600">Operational</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-700">{stats?.warning_assets || 0}</p>
                  <p className="text-sm text-amber-600">Warning</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-700">{stats?.critical_assets || 0}</p>
                  <p className="text-sm text-red-600">Critical</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-2xl font-bold text-slate-700">{stats?.offline_assets || 0}</p>
                  <p className="text-sm text-slate-600">Offline</p>
                </div>
              </div>

              {/* Asset Type Breakdown */}
              <div className="space-y-3 pt-4 border-t border-slate-100">
                <p className="text-sm font-medium text-slate-600">By Asset Type</p>
                {stats?.asset_breakdown &&
                  Object.entries(stats.asset_breakdown).map(([type, count]) => {
                    const Icon = assetIcons[type] || Zap;
                    return (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="capitalize text-sm">{type}</span>
                        </div>
                        <span className="font-medium">{count}</span>
                      </div>
                    );
                  })}
              </div>

              <Button variant="outline" className="w-full mt-4" asChild>
                <Link to="/assets" data-testid="view-all-assets">
                  View All Assets
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Alerts & Work Orders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Critical Alerts */}
          <Card className="border-slate-200" data-testid="critical-alerts">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg flex items-center justify-between">
                Critical Alerts
                <Badge variant="destructive">{alerts.filter((a) => a.severity === 'critical').length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 divide-y divide-slate-100">
              {alerts.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No active alerts</p>
              ) : (
                alerts.map((alert) => (
                  <div key={alert.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-1.5 rounded-full ${
                          alert.severity === 'critical'
                            ? 'bg-red-100'
                            : alert.severity === 'warning'
                            ? 'bg-amber-100'
                            : 'bg-sky-100'
                        }`}
                      >
                        <AlertTriangle
                          className={`w-4 h-4 ${
                            alert.severity === 'critical'
                              ? 'text-red-600'
                              : alert.severity === 'warning'
                              ? 'text-amber-600'
                              : 'text-sky-600'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-slate-900">
                            {alert.asset_name}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-xs ${
                              alert.severity === 'critical'
                                ? 'border-red-300 text-red-700 bg-red-50'
                                : alert.severity === 'warning'
                                ? 'border-amber-300 text-amber-700 bg-amber-50'
                                : 'border-sky-300 text-sky-700 bg-sky-50'
                            }`}
                          >
                            {alert.severity.toUpperCase()}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-1">{alert.title}</p>
                        {alert.ai_recommendation && (
                          <div className="mt-2 p-2 bg-sky-50 border border-sky-200 rounded-lg">
                            <p className="text-xs font-medium text-sky-800">AI Recommendation:</p>
                            <p className="text-xs text-sky-700 line-clamp-2">{alert.ai_recommendation}</p>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-red-600">
                          -{formatCurrency(alert.revenue_impact)}
                        </p>
                        <p className="text-xs text-slate-500">per day</p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="mt-3 w-full" data-testid={`create-wo-${alert.id}`}>
                      Create Work Order
                    </Button>
                  </div>
                ))
              )}
              <Button variant="ghost" className="w-full mt-4" asChild>
                <Link to="/alerts" data-testid="view-all-alerts">
                  View All Alerts
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          {/* Active Work Orders */}
          <Card className="border-slate-200" data-testid="active-workorders">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="font-heading text-lg flex items-center justify-between">
                Active Work Orders
                <Badge>{stats?.open_work_orders || 0}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 divide-y divide-slate-100">
              {workOrders.length === 0 ? (
                <p className="text-sm text-slate-500 py-4 text-center">No active work orders</p>
              ) : (
                workOrders.map((wo) => {
                  const completedTasks = wo.tasks?.filter((t) => t.completed).length || 0;
                  const totalTasks = wo.tasks?.length || 1;
                  const progress = Math.round((completedTasks / totalTasks) * 100);

                  return (
                    <div key={wo.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-slate-900">
                              {wo.wo_number}
                            </span>
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                wo.priority === 'critical'
                                  ? 'border-red-300 text-red-700 bg-red-50'
                                  : wo.priority === 'high'
                                  ? 'border-orange-300 text-orange-700 bg-orange-50'
                                  : wo.priority === 'medium'
                                  ? 'border-amber-300 text-amber-700 bg-amber-50'
                                  : 'border-slate-300 text-slate-700'
                              }`}
                            >
                              {wo.priority.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{wo.title}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {wo.asset_name} • {wo.assigned_to}
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={`${
                            wo.status === 'in_progress'
                              ? 'border-sky-300 text-sky-700 bg-sky-50'
                              : wo.status === 'scheduled'
                              ? 'border-amber-300 text-amber-700 bg-amber-50'
                              : 'border-slate-300 text-slate-600'
                          }`}
                        >
                          {wo.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                          <span>
                            {completedTasks}/{totalTasks} tasks
                          </span>
                          <span>{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-1.5" />
                      </div>
                    </div>
                  );
                })
              )}
              <Button variant="ghost" className="w-full mt-4" asChild>
                <Link to="/work-orders" data-testid="view-all-workorders">
                  View All Work Orders
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
